import React, { FC, useCallback, useMemo, useState } from "react";
import { SortingOrder } from "@shared/types";
import { ColumnType } from "@shared/common/CardsList/types";
import {
  CardSortersProps,
  ExpandCollapseDirection,
} from "@shared/common/CardSorters/types";
import { useRefValue } from "@shared/utils/hooks/misc";

interface useCardSortersProps<RecordType extends { key: string | number }> {
  CardSorters: FC<CardSortersProps>;
  onExpandAll?: () => void;
  onCollapseAll?: () => void;
  keys: (string | number)[];
  expandedKeys?: (string | number)[];
  style?: any;
  columns: Omit<ColumnType<RecordType>, "render">[];
  data?: RecordType[];
  showExpandCollapse?: boolean;
  defaultSortFunction?: (a: RecordType, b: RecordType) => number;
}

const useCardSorters = <RecordType extends { key: string | number } = any>({
  CardSorters,
  onExpandAll,
  onCollapseAll,
  keys,
  expandedKeys,
  style,
  columns,
  data,
  showExpandCollapse,
  defaultSortFunction,
}: useCardSortersProps<RecordType>) => {
  const [currentExpandCollapseDirection, setCurrentExpandCollapse] = useState(
    ExpandCollapseDirection.EXPAND
  );
  const defaultSortColumn = useMemo(
    () => columns?.find((column) => column.defaultSortOrder),
    [columns]
  );
  const [sortOrder, setSortOrder] = useState<SortingOrder>(
    defaultSortColumn?.defaultSortOrder || SortingOrder.ASCEND
  );
  const [sortColumnKey, setSortColumnKey] = useState<
    string | number | undefined
  >(defaultSortColumn?.key);

  const expandAll = () => {
    setCurrentExpandCollapse(ExpandCollapseDirection.COLLAPSE);
    onExpandAll?.();
  };

  const collapseAll = () => {
    setCurrentExpandCollapse(ExpandCollapseDirection.EXPAND);
    onCollapseAll?.();
  };

  const refValues = useRefValue({
    expandAll,
    collapseAll,
    expandedKeys,
    currentExpandCollapseDirection,
    keys,
  });

  const onExpandCollapseAllClick = useCallback(() => {
    if (refValues.current.expandedKeys?.length === 0)
      refValues.current.expandAll();
    else if (
      refValues.current.expandedKeys?.length === refValues.current.keys.length
    )
      refValues.current.collapseAll();
    else if (
      refValues.current.currentExpandCollapseDirection ===
      ExpandCollapseDirection.COLLAPSE
    )
      refValues.current.collapseAll();
    else if (
      refValues.current.currentExpandCollapseDirection ===
      ExpandCollapseDirection.EXPAND
    )
      refValues.current.expandAll();
  }, []);

  const cardsSorters = useMemo(() => {
    return (
      <CardSorters
        onExpandCollapseClick={onExpandCollapseAllClick}
        columns={columns}
        sortColumnKey={sortColumnKey}
        sortOrder={sortOrder}
        setSortColumnKey={setSortColumnKey}
        setSortOrder={setSortOrder}
        showExpandCollapse={showExpandCollapse}
        style={style}
      />
    );
  }, [
    showExpandCollapse,
    sortOrder,
    sortColumnKey,
    columns,
    onExpandCollapseAllClick,
  ]);

  const sortFunction = useMemo(
    () =>
      columns.find((column) => column.key === sortColumnKey)?.sorter ||
      defaultSortFunction,
    [columns, sortColumnKey, defaultSortFunction]
  );

  const sortedData = useMemo(() => {
    if (sortFunction) {
      return data?.slice().sort((a, b) => sortFunction(a, b) * sortOrder);
    }
    return data;
  }, [data, sortFunction, sortOrder]);

  return { sortOrder, sortColumnKey, cardsSorters, sortedData };
};

export default useCardSorters;
